import React from "react";
import { Box } from "@mui/material";
import { QuizHeader } from "../QuizHeader";
import { QuizTabProgress, QuizTabIndex } from "./QuizTabProgress";
import { QUIZ } from "@/theme/quizTokens";
import { quizAsset } from "./quizAssets";

/** Bottom-anchored dog cut-out photo (dachshund / frenchie / collie screens). */
export type QuizCutout = {
  src: string;
  side: "left" | "right";
  /** Rendered width (px) per breakpoint. */
  width: { xs: number; md: number };
  /** Bottom padding reserved on the content column so text never sits under the photo. */
  clearance?: { xs: number; md: number };
};

type Props = {
  activeTab: QuizTabIndex;
  onClose?: () => void;
  /**
   * Chrome variant per Figma:
   *  - "hero"    → food-bowl photo band (dog-count + name screens). Mobile: nav → photo →
   *                tabs (no ✕). Desktop: nav → tabs+✕ → rounded photo card.
   *  - "plain"   → question screens: nav → tabs+✕ directly (both breakpoints).
   *  - "profile" → blue band with dog photo ABOVE the tabs (both breakpoints).
   */
  variant?: "hero" | "plain" | "profile";
  /**
   * Image for the hero/profile band. Pass `{ xs, md }` for dedicated
   * mobile/desktop crops (the Figma frames use different images per breakpoint).
   * Defaults to the food-bowl photo.
   */
  heroSrc?: string | { xs: string; md: string };
  /** Solid background behind the hero image (profile screen = blue). */
  heroBg?: string;
  /** Overlay rendered on the profile band (e.g. the review step's 94% roundel). */
  heroOverlay?: React.ReactNode;
  /** Fine-tune the profile band photo crop per screen. */
  heroPosition?: { xs: string; md: string };
  /** Support block (Did you know? / Trustpilot quote) pushed toward the bottom. */
  support?: React.ReactNode;
  /** Bottom-anchored dog cut-out photo. */
  cutout?: QuizCutout;
  /** Max width of the content area (px on desktop). Questions use 600; results screens wider. */
  contentMaxWidth?: number;
  children: React.ReactNode;
};

/**
 * Shared chrome for the redesigned quiz: brown nav → (variant band) → 4-tab progress →
 * question content on a cream canvas. The sticky Previous/Next bar (`QuizFooter`) is
 * rendered separately as a fixed element; this shell reserves bottom padding for it
 * via the `--quizFooterH` CSS var.
 */
export function QuizShell({
  activeTab,
  onClose,
  variant = "plain",
  heroSrc,
  heroBg,
  heroOverlay,
  heroPosition,
  support,
  cutout,
  contentMaxWidth = 600,
  children,
}: Props) {
  const isHero = variant === "hero";
  const isProfile = variant === "profile";

  /**
   * Render the band photo. With dedicated per-breakpoint crops we use a single
   * <picture> element — the browser resolves exactly one image, so no theme CSS
   * can ever make both crops render at once.
   */
  const bandImage = (
    src: string | { xs: string; md: string },
    position?: { xs: string; md: string }
  ) => {
    const imgSx = {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "block",
      pointerEvents: "none",
    } as const;
    if (typeof src === "string") {
      return (
        <Box
          component="img"
          src={src}
          alt=""
          sx={{ ...imgSx, objectPosition: position ?? "center" }}
        />
      );
    }
    return (
      <Box component="picture" sx={{ display: "block", width: "100%", height: "100%" }}>
        <source media="(min-width: 900px)" srcSet={src.md} />
        <Box
          component="img"
          src={src.xs}
          alt=""
          sx={{
            ...imgSx,
            objectPosition: position ?? "center",
          }}
        />
      </Box>
    );
  };

  /* Food-bowl hero band. Mobile: full-bleed under nav. Desktop: rounded card under tabs. */
  const heroBand = isHero && (
    <Box
      sx={{
        order: { xs: 1, md: 2 },
        width: { xs: "100%", md: 600 },
        maxWidth: "100%",
        mx: "auto",
        height: { xs: QUIZ.layout.heroMobile, md: 247 },
        borderRadius: { xs: 0, md: "8px" },
        overflow: "hidden",
        bgcolor: heroBg,
        flexShrink: 0,
      }}
    >
      {bandImage(
        heroSrc ?? { xs: quizAsset("hero-food"), md: quizAsset("hero-food-d") },
        heroPosition ?? { xs: "center 35%", md: "center" }
      )}
    </Box>
  );

  /* Profile band: photo full-bleed above the tabs on both breakpoints (Figma: 194 / 216). */
  const profileBand = isProfile && (
    <Box
      sx={{
        order: 0,
        position: "relative",
        width: "100%",
        height: { xs: 194, md: 216 },
        flexShrink: 0,
      }}
    >
      <Box sx={{ width: "100%", height: "100%", bgcolor: heroBg ?? QUIZ.color.blue, overflow: "hidden" }}>
        {bandImage(
          heroSrc ?? {
            xs: quizAsset("hero-profile-dog"),
            md: quizAsset("hero-profile-dog-d"),
          },
          heroPosition ?? { xs: "center 30%", md: "center" }
        )}
      </Box>
      {heroOverlay}
    </Box>
  );

  return (
    <Box
      sx={{
        "--quizFooterH": {
          xs: `${QUIZ.layout.bottomBarMobile}px`,
          md: `${QUIZ.layout.bottomBarDesktop}px`,
        },
        position: "relative",
        minHeight: "100dvh",
        width: "100%",
        bgcolor: QUIZ.color.cream,
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
        pb: "calc(var(--quizFooterH) + env(safe-area-inset-bottom))",
      }}
    >
      <QuizHeader />

      <Box
        sx={{
          flex: 1,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: { xs: "5px", md: isHero ? "40px" : "0px" },
          pt: 0,
          pb: { xs: "24px", md: "48px" },
        }}
      >
        {profileBand}
        {heroBand}

        <Box
          sx={{
            order: isProfile ? 1 : { xs: 2, md: 1 },
            width: "100%",
            display: "flex",
            justifyContent: "center",
            pt: isProfile ? { xs: "12px", md: "16px" } : { xs: 0, md: "56px" },
          }}
        >
          <QuizTabProgress
            activeTab={activeTab}
            onClose={onClose}
            // Mobile hero screens have no ✕ in the Figma; all other chromes do.
            hideCloseOnMobile={isHero}
          />
        </Box>

        <Box
          sx={{
            order: 3,
            width: "100%",
            maxWidth: { xs: "100%", md: contentMaxWidth },
            mx: "auto",
            px: { xs: "23px", md: "32px" },
            pt: isHero ? 0 : isProfile ? { xs: "20px", md: "36px" } : { xs: "34px", md: "72px" },
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            position: "relative",
            zIndex: 1,
          }}
        >
          {children}

          {support && (
            <Box
              sx={{
                // With a cut-out the quote/fact sits right after the question;
                // otherwise it drifts toward the bottom of the canvas (Figma).
                mt: cutout ? { xs: "44px", md: "60px" } : "auto",
                pt: cutout ? 0 : { xs: "40px", md: "56px" },
                pb: { xs: "8px", md: "16px" },
                display: "flex",
                justifyContent: "center",
              }}
            >
              {support}
            </Box>
          )}

          {cutout?.clearance && (
            <Box sx={{ height: { xs: cutout.clearance.xs, md: cutout.clearance.md }, flexShrink: 0 }} />
          )}
        </Box>

        {cutout && (
          <Box
            component="img"
            src={cutout.src}
            alt=""
            sx={{
              position: "absolute",
              bottom: 0,
              [cutout.side]: 0,
              width: { xs: cutout.width.xs, md: cutout.width.md },
              maxWidth: "80%",
              display: "block",
              pointerEvents: "none",
              zIndex: 0,
              order: 4,
            }}
          />
        )}
      </Box>
    </Box>
  );
}

export default QuizShell;
